import React from 'react';
import styled from 'styled-components';
import {
    ArrowDownToLine,
    ArrowUpToLine,
    ChevronDown,
    MoreHorizontal,
    Trash2,
    Scissors,
    RotateCw,
    Download,
    RotateCcw,
} from 'lucide-react';

/** One row in the actions menu. The menu is always visible; individual items
 *  may be disabled when their target is missing (e.g., no selection).
 *  `parenthetical` is rendered as muted text at the far right. */
export interface PageActionItem {
    key: string;
    label: string;
    parenthetical?: string;
    tone?: 'default' | 'danger';
    disabled?: boolean;
    disabledHint?: string;
    /** Optional per-item icon; falls back to the key-based ACTION_ICONS map. */
    icon?: React.ReactNode;
    onSelect: () => void;
}

/** Visual divider rendered between items. */
export interface PageActionSeparator {
    key: string;
    separator: true;
}

export type PageActionEntry = PageActionItem | PageActionSeparator;

export interface PageActionsConfig {
    /** Disable the whole menu while an action is in flight. */
    disabled?: boolean;
    items: PageActionEntry[];
}

export const isSeparator = (entry: PageActionEntry): entry is PageActionSeparator =>
    (entry as PageActionSeparator).separator === true;

const ACTION_ICONS: Record<string, React.ReactNode> = {
    'export': <Download size={16} />,
    'reprocess': <RotateCcw size={16} />,
    'merge-prev': <ArrowUpToLine size={16} />,
    'merge-next': <ArrowDownToLine size={16} />,
    'delete-selected': <Trash2 size={16} />,
    'split-selected': <Scissors size={16} />,
    'split-current': <Scissors size={16} />,
    'rotate': <RotateCw size={16} />,
};

const Wrapper = styled.div`
    position: relative;
    display: inline-flex;
`;

const Button = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    background: #fff;
    color: #333;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;

    &:hover:not(:disabled) {
        background: #f5f5f5;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Menu = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 220px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
    padding: 6px 0;
    z-index: 60;
`;

const MenuItemButton = styled.button<{ $tone?: 'default' | 'danger' }>`
    width: 100%;
    background: none;
    border: none;
    padding: 8px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    color: ${({ $tone }) => ($tone === 'danger' ? '#b91c1c' : '#1e293b')};
    cursor: pointer;
    font-family: inherit;
    text-align: left;

    &:hover:not(:disabled) {
        background: #f8fafc;
    }

    &:disabled {
        color: #94a3b8;
        cursor: not-allowed;
    }
`;

const MenuIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    flex-shrink: 0;
    color: currentColor;
    opacity: 0.7;
`;

const MenuItemLabel = styled.span`
    flex: 1 1 auto;
    min-width: 0;
`;

const MenuItemParenthetical = styled.span`
    flex-shrink: 0;
    margin-left: 12px;
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
    font-variant-numeric: tabular-nums;
`;

const MenuSeparator = styled.div`
    height: 1px;
    margin: 4px 8px;
    background: #eef2f7;
`;

interface ActionsMenuProps {
    config?: PageActionsConfig;
    /** Trigger button label (default "Actions"). */
    label?: string;
    /** Title shown when the menu has no enabled items. */
    emptyTitle?: string;
}

export const ActionsMenu: React.FC<ActionsMenuProps> = ({
    config,
    label = 'Actions',
    emptyTitle = 'No actions available',
}) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (!open) return;
        const onClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    if (!config) return null;

    const hasAnyEnabledItem = config.items.some(
        entry => !isSeparator(entry) && !entry.disabled,
    );
    const disabled = config.disabled || !hasAnyEnabledItem;

    return (
        <Wrapper ref={ref}>
            <Button
                type="button"
                onClick={() => setOpen(o => !o)}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={open}
                title={disabled ? emptyTitle : label}
            >
                <MoreHorizontal size={16} />
                {label}
                <ChevronDown size={14} />
            </Button>
            {open && !disabled && (
                <Menu role="menu">
                    {config.items.map(entry => {
                        if (isSeparator(entry)) {
                            return <MenuSeparator key={entry.key} role="separator" />;
                        }
                        const icon = entry.icon ?? ACTION_ICONS[entry.key] ?? null;
                        return (
                            <MenuItemButton
                                key={entry.key}
                                type="button"
                                role="menuitem"
                                $tone={entry.tone ?? 'default'}
                                disabled={entry.disabled || config.disabled}
                                title={entry.disabled ? entry.disabledHint : undefined}
                                onClick={() => {
                                    setOpen(false);
                                    entry.onSelect();
                                }}
                            >
                                <MenuIcon>{icon}</MenuIcon>
                                <MenuItemLabel>{entry.label}</MenuItemLabel>
                                {entry.parenthetical && (
                                    <MenuItemParenthetical>{entry.parenthetical}</MenuItemParenthetical>
                                )}
                            </MenuItemButton>
                        );
                    })}
                </Menu>
            )}
        </Wrapper>
    );
};
